import { get, includes, unset } from "lodash";
import { ApolloClient, ApolloLink, HttpLink } from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { isMutationOperation } from "@apollo/client/utilities";

import moment from "moment";
import type { Moment } from "moment";
import { matchPaths } from "src/consts/urlPaths";
import { isTeamAllocationsPage } from "src/util/url";
import { API_DATE_FORMAT } from "src/consts/global";
import { isEnvTrue } from "src/services/env";
import { reportError } from "../services/errorReporting";
import cache from "./cache";

const CUSTOM_HEADERS_STORAGE_KEY = "teamform_apollo_custom_headers";

// Headers that must not be overridden by user-controlled localStorage values.
const PROTECTED_HEADERS = new Set([
  "authorization",
  "x-workspace-id",
  "x-tenant-id",
  "x-pit-date",
]);

const getCustomHeadersFromStorage = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(CUSTOM_HEADERS_STORAGE_KEY);
    if (!stored) return {};
    const parsed: Record<string, string> = JSON.parse(stored);
    // Strip any keys that overlap with protected headers to prevent localStorage
    // debugging overrides from tampering with auth/tenant context.
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([key]) => !PROTECTED_HEADERS.has(key.toLowerCase())
      )
    );
  } catch {
    return {};
  }
};

export const NO_WORKSPACE_ID = "NO_WORKSPACE_ID";

const operationsNotToBeRetried = ["createWorkspace"];

const READONLY_ALLOWED_MUTATIONS = ["setSupportImpersonation"];

export interface DateSettings {
  fromDate?: Moment;
  toDate?: Moment;
  comparisonMode?: boolean;
}

export interface OverrideTenant {
  tenantId: string;
  canRunMutation: boolean;
}

export interface ApolloClientOptions {
  apiUrl: string;
  getTokenSilently: () => Promise<string>;
  loginWithRedirect?: (opts: {
    appState: { targetUrl: string };
  }) => Promise<void>;
  overrideTenant?: OverrideTenant;
  workspaceId?: string;
  getDateSettings?: () => DateSettings | null | undefined;
}

const isAuthTokenError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message.toLowerCase() : "";
  return (
    msg === "timeout" || msg === "login_required" || msg === "login required"
  );
};

export default ({
  getTokenSilently,
  loginWithRedirect,
  overrideTenant,
  workspaceId,
  getDateSettings,
  apiUrl,
}: ApolloClientOptions): ApolloClient => {
  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 2,
      retryIf: (error, operation) => {
        if (includes(operationsNotToBeRetried, operation?.operationName)) {
          return false;
        }
        if (isAuthTokenError(error)) {
          return false;
        }

        reportError(error, {
          retrying: true,
          operation,
        });
        return true;
      },
    },
  });

  const httpLink = new HttpLink({
    uri: `${apiUrl}/graphql`,
  });

  const authLink = new SetContextLink(async (previousContext, _operation) => {
    const { headers } = previousContext;
    let token;
    try {
      token = await getTokenSilently();
    } catch (error) {
      if (isAuthTokenError(error)) {
        try {
          if (loginWithRedirect) {
            await loginWithRedirect({
              appState: {
                targetUrl: window.location.pathname + window.location.search,
              },
            });
          }
        } catch (redirectError) {
          reportError(redirectError, {
            context: "loginWithRedirect",
            originalError: error,
          });
        }
      }
      throw error;
    }

    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    };
  });

  const customHeadersLink = new SetContextLink(
    (previousContext, _operation) => {
      const { headers } = previousContext;
      const customHeaders = getCustomHeadersFromStorage();

      return {
        headers: {
          ...headers,
          ...customHeaders,
        },
      };
    }
  );

  const pitLink = new SetContextLink(async (previousContext, _operation) => {
    const { headers } = previousContext;

    // PLAT-1249: respect a per-operation x-pit-date (e.g. org chart diff
    // baseline/next loads). The global page date must not override it, or both
    // pits resolve to the same date and the diff is always empty.
    if (headers?.["x-pit-date"]) {
      return previousContext;
    }

    const urlMatch = matchPaths();

    const userAssignedDate = get(getDateSettings?.(), "toDate") as
      | Moment
      | undefined;
    let pitDate: Moment;
    if (
      isTeamAllocationsPage(urlMatch) ||
      !userAssignedDate ||
      !userAssignedDate.isValid()
    ) {
      pitDate = moment();
    } else {
      pitDate = userAssignedDate;
    }

    const pitDateHeader = {
      "x-pit-date": pitDate.format(API_DATE_FORMAT),
    };

    return {
      ...previousContext,
      headers: {
        ...headers,
        ...pitDateHeader,
      },
    };
  });

  const workspaceIdLink = new SetContextLink(
    async (previousContext, _operation) => {
      const { headers } = previousContext;
      const workspaceIdInUrl = matchPaths()?.params?.["workspaceId"];

      let workspaceHeader: Record<string, string> | null = null;
      if (headers["x-workspace-id"] !== NO_WORKSPACE_ID) {
        const headerWorkspaceId =
          workspaceId ||
          workspaceIdInUrl ||
          process.env["REACT_APP_WORKSPACE_ID"];
        workspaceHeader = headerWorkspaceId
          ? { "x-workspace-id": headerWorkspaceId }
          : null;
      } else {
        unset(headers, "x-workspace-id");
      }

      return {
        ...previousContext,
        headers: {
          ...headers,
          ...workspaceHeader,
        },
      };
    }
  );

  const links: ApolloLink[] = [
    retryLink,
    authLink,
    customHeadersLink,
    workspaceIdLink,
    pitLink,
    httpLink,
  ];

  if (overrideTenant) {
    const overrideTenantValidationLink = new ApolloLink(
      (operation, forward) => {
        if (!overrideTenant?.canRunMutation) {
          if (isMutationOperation(operation.query)) {
            const name = operation.operationName;
            const allowed = name && includes(READONLY_ALLOWED_MUTATIONS, name);
            if (!allowed) {
              throw new Error("Readonly mode, can not run mutations");
            }
          }
        }

        return forward(operation);
      }
    );
    links.unshift(overrideTenantValidationLink);
  }

  const client = new ApolloClient({
    link: ApolloLink.from(links),
    cache,
    devtools: { enabled: isEnvTrue(process.env["REACT_APP_IS_OFFLINE"]) },
  });
  return client;
};
